import Slider from 'react-slick';
import React, { useEffect, useState } from 'react';
import breakpoints from '../../../styles/partials/breakpoints.module.scss';
import styles from './media-gallery.module.scss';
import useWindowResize from '../../../hooks/use-window-resize.hook';
import { deviceDetect } from '../../../data/device-detect';
import ImageComponent from '../../../components/atoms/image-component/image-component';
import Video from '../../../components/atoms/video/video';
import { PlayCircleRegularWhiteIcon } from '../../atoms/icons/icons';
import IImage from '../../../models/viewModels/base/IImage';
import { IMediaGallerySettings } from '../../../models/IMediaGallerySettings';
import IVideo from '../../../models/viewModels/base/IVideo';
import getMediaGallerySettings from './media-gallery.settings';
import { Breakpoint } from '../../../constants/breakpoints';

export type Props = {
  galleryItems: Array<IImage | IVideo>;
  mainSliderSettings?: IMediaGallerySettings;
  thumbSliderSettings?: IMediaGallerySettings;
  hash?: string;
  videoPreload?: boolean;
};

export const breakpointDesktop = Breakpoint.Large - 1;
export const breakpointTabletLandscape = parseInt(breakpoints.tablet_landscape, 10) - 1;

const filmstripImageWidth = `(max-width: ${breakpoints.md}) 100px, 250px`;

export function buildVideoPosterUrl(videoSrc: string): string {
  return videoSrc.replace(/\.[^.]*$/, `.jpg`);
}

export default function MediaGallery({
  galleryItems,
  mainSliderSettings,
  thumbSliderSettings,
  hash,
  videoPreload,
}: Props) {
  if (hash) {
    const hashTagIndex = galleryItems.findIndex((g) => g.id === hash.replace('#', ''));
    if (hashTagIndex > 1) {
      const selectedItem = galleryItems.splice(hashTagIndex, 1)[0];
      galleryItems.splice(0, 1, selectedItem);
    }
  }

  const defaultSettings = getMediaGallerySettings(galleryItems);
  mainSliderSettings = mainSliderSettings ?? defaultSettings.mainSliderSettings;
  thumbSliderSettings = thumbSliderSettings ?? defaultSettings.thumbSliderSettings;

  const [mainSliderNavFor, setMainSliderNavFor] = useState(null);
  const [thumbSliderNavFor, setThumbSliderNavFor] = useState(null);
  const [activeSlideIndex, setActiveSlideIndex] = useState<number>(0);
  const [isCurrentSlideVideo, setIsCurrentSlideVideo] = useState<boolean>(false);

  const [isTabletBreakpointDown, setIsTabletBreakpointDown] = useState(deviceDetect.isMobile);
  const [isDesktopBreakpointDown, setIsDesktopBreakpointDown] = useState(deviceDetect.isMobile);

  useWindowResize((newWidth) => {
    setIsTabletBreakpointDown(newWidth < parseInt(breakpoints.tablet_landscape, 10));
    setIsDesktopBreakpointDown(newWidth < Breakpoint.Large);
  }, 100);

  const defaultVideoRatio = { height: 183, width: 326 };

  function thumbsOverflowVisible(): boolean {
    const isTabletOverflowNeeded =
      isDesktopBreakpointDown &&
      !(
        thumbSliderSettings?.responsive?.find((x) => x.breakpoint === breakpointDesktop)?.settings
          ?.infinite ?? true
      );
    const isTabletLandscapeOverflowNeeded =
      isTabletBreakpointDown &&
      !isDesktopBreakpointDown &&
      !(
        thumbSliderSettings?.responsive?.find((x) => x.breakpoint === breakpointTabletLandscape)
          ?.settings?.infinite ?? true
      );

    return isTabletOverflowNeeded || isTabletLandscapeOverflowNeeded;
  }

  function updateCurrentSlideVideoStatus(slideIndex = 0): void {
    setIsCurrentSlideVideo(galleryItems[slideIndex].resourceType === `video`);
  }

  const beforeChange = (_oldIndex: number, newIndex: number) => {
    setActiveSlideIndex(newIndex);
    updateCurrentSlideVideoStatus(newIndex);
  };

  useEffect(() => {
    updateCurrentSlideVideoStatus();
  }, [galleryItems]);

  return (
    <section className={`${styles.gallery} mb-md-4 mb-3`}>
      <div
        className={`${styles[`main-slider-wrapper`]} ${isCurrentSlideVideo ? styles[`main-slider-wrapper-video`] : ``}`}
        aria-label='carousel'
        role='region'
      >
        <Slider
          {...mainSliderSettings}
          asNavFor={thumbSliderNavFor}
          beforeChange={beforeChange}
          ref={(slider) => setMainSliderNavFor(slider)}
        >
          {galleryItems?.map((galleryItem, index) => (
            <div
              key={galleryItem.secureUrl}
              className={`${styles[`slick-slide`]} ${styles[`slick-slide-main`]} slick-slide
              ${(galleryItems[activeSlideIndex] as IVideo)?.title ? styles[`partial-radius`] : styles[`full-radius`]}`}
              role='group'
              aria-label={`slide ${index + 1}`}
            >
              {galleryItem.resourceType === `image` && (
                <ImageComponent
                  src={galleryItem.secureUrl}
                  altText={`${(galleryItem as IImage)?.alt ?? ``}`}
                  id={galleryItem.id}
                />
              )}
              {galleryItem.resourceType === `video` && (
                <Video
                  videoPreload={videoPreload}
                  src={galleryItem.secureUrl}
                  videoRatio={defaultVideoRatio}
                  poster={
                    (galleryItem as IVideo)?.previewImageSecureURL
                      ? (galleryItem as IVideo).previewImageSecureURL
                      : buildVideoPosterUrl(galleryItem.secureUrl)
                  }
                  id={galleryItem.id}
                />
              )}
            </div>
          ))}
        </Slider>
        <div
          style={{
            display:
              (galleryItems[activeSlideIndex] as IVideo)?.title ||
              (galleryItems[activeSlideIndex] as IImage)?.title
                ? `block`
                : `none`,
          }}
          className={`${styles.caption}`}
        >
          <h3>
            {(galleryItems[activeSlideIndex] as IVideo)?.title
              ? (galleryItems[activeSlideIndex] as IVideo)?.title
              : (galleryItems[activeSlideIndex] as IImage)?.title}
          </h3>
          <p>
            {(galleryItems[activeSlideIndex] as IVideo)?.caption
              ? (galleryItems[activeSlideIndex] as IVideo)?.caption
              : (galleryItems[activeSlideIndex] as IImage)?.caption}
          </p>
        </div>
      </div>
      {galleryItems.length > 1 && (
        <div
          className={`${styles[`thumbnail-slider-wrapper`]} thumbnail-slider-wrapper carousel_custom-navs ${thumbsOverflowVisible() ? styles[`overflow-visible`] : ``}`}
          id='thumb-list'
          aria-label='thumbnail carousel'
          role='region'
        >
          <Slider
            {...thumbSliderSettings}
            asNavFor={mainSliderNavFor}
            ref={(slider) => setThumbSliderNavFor(slider)}
          >
            {galleryItems?.map((galleryItem, index) => (
              <div
                className={`${styles[`thumb-slick-slide`]} slick-slide thumb-slick-slide`}
                key={galleryItem.secureUrl}
                role='group'
                aria-label={`slide ${index + 1}`}
              >
                <div className={`${styles[`thumb-wrapper`]} thumb-wrapper`}>
                  {galleryItem.resourceType === `image` && (
                    <ImageComponent
                      src={galleryItem.secureUrl}
                      altText={`${(galleryItem as IImage)?.alt ?? ``}`}
                      sizes={filmstripImageWidth}
                    />
                  )}
                  {galleryItem.resourceType === `video` && (
                    <div>
                      <PlayCircleRegularWhiteIcon />
                      <ImageComponent
                        src={
                          (galleryItem as IVideo)?.previewImageSecureURL
                            ? (galleryItem as IVideo).previewImageSecureURL
                            : buildVideoPosterUrl(galleryItem.secureUrl)
                        }
                        altText={`${(galleryItem as IVideo)?.previewImageAlt ? (galleryItem as IVideo).previewImageAlt : ((galleryItem as IImage)?.alt ?? ``)}`}
                        sizes={filmstripImageWidth}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </Slider>
        </div>
      )}
    </section>
  );
}
